/* eslint complexity: 0 */

import React, { MutableRefObject, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Link, LinkButton, Typography } from '@vtblife/uikit';

import {
    ApplicationIdEnum,
    DeveloperCard,
    NewBuildingConstructionProgress,
    NewBuildingWhitelabelEnum,
    RouteParams
} from '@search/graphql-typings';

import { useStaticPathConfig } from '@search/common-core/src/StaticPathContext';
import classname from '@search/classname/src';
import Breadcrumb from '@search/vtbeco-ui/src/components/Breadcrumb';

import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { isM2ProUser } from '@search/auth/src/helpers/isM2ProUser';
import { DATA_GTM } from '@search/vtbeco-frontend-core/domain/newbuilding/libs/constants';
import { dataLayerPush } from '@search/vtbeco-frontend-core/domain/google/dataLayerPush';
import { useMinimalBanksMortgageOffersSliced } from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/MinimalBanksMortgageOffers/MinimalBanksMortgageOffers';
import { Mortgage2StarterModal } from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/mortgage2/starter/Mortgage2StarterModal';
import type { UseBankMortgageFormProps } from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/MinimalBanksMortgageFields/useBankMortgageForm';

import {
    ImageLazySrcSetProvide,
    useClImageLazySrcSetUrl
} from '@search/vtbeco-frontend-core/domain/image/lazy/ImageLazySrcSet';

import { INewBuildingCardRightBlockProps } from '../../../../instances/m2/components/desktop/NewBuildingCard/NewBuildingCardRightBlock';
import { INewBuildingCardTopStickyProps } from '../../../../instances/vtb/components/desktop/NewBuildingCard/NewBuildingCardTopSticky';
import { useDeveloperNames } from '../../../common/components/NewBuildingCard';
import { GlobalContext, IOnChangeGlobalContext, OnChangeGlobalContext } from '../../../common/components/GlobalContext';
import { PhoneButtonStateProvider } from '../../../common/components/NewBuildingCard/PhoneButtonStateProvider';
import { ChatStateProvider } from '../../../common/components/NewBuildingCard/ChatStateProvider';
import { useBreadcrumbs } from '../../../common/components/NewBuildingCardPage/useBreadcrumbs';
import type { NewBuildingConstructionQuery$data as NewBuildingConstructionQueryResponse } from '../../../common/components/NewBuildingConstruction/__generated__/NewBuildingConstructionQuery.graphql';
import { usePhoneButton } from '../../../common/hooks/usePhoneButton';
import useMortgageDemandLink from '../../../common/hooks/useMortgageDemandLink';

import NewBuildingCardHeading from '../NewBuildingCard/NewBuildingCardHeading';

import ConstructionProgress from './NewBuildingConstructionProgress';

import './styles.css';

interface INewBuildingConstructionProps {
    newBuilding: NewBuildingConstructionQueryResponse['newBuilding'];
    isLoading: boolean;
    cardURL: string;
}

const cn = classname.bind(null, 'NewBuildingConstruction');

const NewBuildingConstruction: React.FunctionComponent<INewBuildingConstructionProps> = ({
    newBuilding,
    isLoading,
    cardURL
}) => {
    const {
        instanceConfig: {
            hasBreadcrumbs,
            hasStickyNavbar,
            searchIndex
        },
        components: {
            NewBuildingCardRightBlock: NewBuildingCardRightBlockComponent
        }
    } = useContext(GlobalContext);

    const NewBuildingCardRightBlock = NewBuildingCardRightBlockComponent as React.FunctionComponent<INewBuildingCardRightBlockProps>;
    const NewBuildingCardRightBlockVtb = NewBuildingCardRightBlockComponent as React.FunctionComponent<INewBuildingCardTopStickyProps>;

    const staticPath = useStaticPathConfig();
    const onChangeGlobalContext = useContext<IOnChangeGlobalContext>(OnChangeGlobalContext);

    const { auth } = useM2AuthContext();
    const hasRoleM2Pro = isM2ProUser(auth.user?.currentRoleType);

    const breadcrumbs = useBreadcrumbs({
        narrowRegionId: newBuilding.narrowRegionId,
        narrowRegionLocative: newBuilding.narrowRegionLocative,
        routeParams: newBuilding.routeParams as RouteParams,
        title: newBuilding.title.nominative,
        isConstruction: true
    });

    const firstDeveloper = newBuilding.developerCards[0]?.developer;
    const developerLink = useMemo(() => onChangeGlobalContext.linkBuilder.developer &&
        onChangeGlobalContext.linkBuilder.developer({
            id: firstDeveloper!.id,
            name: firstDeveloper!.translitTitle
        }), [ firstDeveloper, onChangeGlobalContext.linkBuilder ]);

    const developerVtb = firstDeveloper?.image?.small ? {
        title: firstDeveloper.title,
        imageUrl: firstDeveloper.image.small
    } : undefined;

    const {
        hasApartments,
        hasApartmentsOnly
    } = newBuilding;

    const { constructionProgress, ranking } = newBuilding;
    const hasConstructionProgress = constructionProgress.length > 0;

    const onStatEventPush = useCallback(() => dataLayerPush({
        event: 'nb_call_like',
        nb_price: newBuilding.priceMin ?? 0,
        nb_class: ranking ?? ''
    }), [ newBuilding.priceMin, ranking ]);

    const phoneButtonState = usePhoneButton({
        newBuildingId: newBuilding.id,
        developerCards: newBuilding.developerCards,
        phoneNum: 0,
        onStatEventPush
    });

    const developerNames = useDeveloperNames(newBuilding.developerCards as DeveloperCard[]);

    const titleSeoTail = useMemo(
        () => newBuilding.seo.seoTitle ? ` (${newBuilding.seo.seoTitle})` : '',
        [ newBuilding.seo.seoTitle ]
    );

    const stickyRef = useRef<HTMLDivElement>(null);

    const [ isOpenMortgageStarterModal, setOpenMortgageStarterModal ] = useState(false);
    const openMortgageStarterModal = useCallback(() => setOpenMortgageStarterModal(true), []);
    const closeMortgageStarterModal = useCallback(() => setOpenMortgageStarterModal(false), []);

    const {
        term,
        propertyCost,
        ownAmount,
        purpose,
        regionId
    } = newBuilding.mortgageParams;

    const {
        offersFull
    } = useMinimalBanksMortgageOffersSliced({
        term,
        propertyCost,
        ownAmount,
        purpose: purpose as UseBankMortgageFormProps['purpose'],
        regionId,
        defaultVisibleOffers: 4
    });

    const mortgageDemandLink = useMortgageDemandLink({
        regionId: newBuilding.mortgageParams.regionId,
        propertyCost: newBuilding.mortgageParams.propertyCost,
        purpose: newBuilding.mortgageParams.purposeValue,
        claimType: newBuilding.mortgageParams.claimType,
        ownAmount: newBuilding.mortgageParams.ownAmount,
        newBuildingName: newBuilding.title.nominative
    });

    const renderMortgageButton = useCallback(() => (
        <div className={cn('mortgageButton')}>
            <LinkButton
                fullWidth
                dataTest='mortgage-send-query'
                variant='accent'
                onClick={hasRoleM2Pro ? undefined : openMortgageStarterModal}
                href={hasRoleM2Pro ? mortgageDemandLink : undefined}
            >
                Купить в ипотеку
            </LinkButton>
        </div>
    ), [ hasRoleM2Pro, mortgageDemandLink, openMortgageStarterModal ]);

    const getUrl = useClImageLazySrcSetUrl({ baseUrl: newBuilding.gallery?.baseUrl });

    if (isLoading) {
        return null;
    }

    return (
        <>
            {hasBreadcrumbs && breadcrumbs && (
                <div className={cn('breadcrumbs')}>
                    <Breadcrumb sections={breadcrumbs.items.map(({
                        title: content,
                        path: href,
                        active
                    }) => ({
                        content,
                        href,
                        active
                    }))} />
                </div>
            )}
            <PhoneButtonStateProvider {...phoneButtonState}>
                <ChatStateProvider initialClientMessage={
                    `Интересует ход строительства ${newBuilding.title.genitive}`
                }>
                    <div className={cn('main')}>
                        <div className={cn('left')}>
                            <NewBuildingCardHeading newBuildingTitle={
                                `Ход строительства ${newBuilding.title.genitive}`
                            } />
                            <Typography
                                className={cn('info')}
                                variant='primary-alone'
                            >
                                <Link href={cardURL}>
                                    На страницу комплекса
                                </Link>
                                {newBuilding.buildingStats.text ? (
                                    <span>
                                        {newBuilding.buildingStats.text}
                                    </span>
                                ) : null}
                            </Typography>
                            {hasConstructionProgress && (
                                <div className={cn('construction')}>
                                    <ImageLazySrcSetProvide getUrl={getUrl}>
                                        <ConstructionProgress
                                            items={constructionProgress as NewBuildingConstructionProgress[]}
                                            title={newBuilding.title}
                                            titleSeoTail={titleSeoTail}
                                        />
                                    </ImageLazySrcSetProvide>
                                </div>
                            )}
                        </div>
                        <div className={cn('right')}>
                            { searchIndex === NewBuildingWhitelabelEnum.Default ? (
                                <NewBuildingCardRightBlock
                                    developerLink={developerLink ?? undefined}
                                    developer={firstDeveloper}
                                    priceMin={newBuilding.priceMin}
                                    priceMax={newBuilding.priceMax}
                                    pricePerM2Min={newBuilding.pricePerM2Min}
                                    pricePerM2Max={newBuilding.pricePerM2Max}
                                    newBuildingId={newBuilding.id}
                                    isMskMO={newBuilding.isMskMO}
                                    isSPbLO={newBuilding.isSPbLO}
                                    isSpecProjectRegion={newBuilding.isSpecProjectRegion}
                                    hasChat={newBuilding.hasChat}
                                    newBuildingName={newBuilding.name}
                                    newBuildingType={newBuilding.type}
                                    newBuildingTitle={newBuilding.title.nominative}
                                    newBuildingAddress={newBuilding.address}
                                    staticPath={staticPath}
                                    buildingClass={newBuilding.buildingClass}
                                    buildingStatus={newBuilding.buildingStatus}
                                    mortgageParams={newBuilding.mortgageParams}
                                    areaRange={newBuilding.formatAreaRange}
                                    developerNames={developerNames}
                                    hasApartments={hasApartments}
                                    hasApartmentsOnly={hasApartmentsOnly}
                                    isSoldOut={newBuilding.isSoldOut}
                                    isSoon={newBuilding.isSoon}
                                    stickyRef={hasStickyNavbar ? stickyRef as MutableRefObject<HTMLDivElement> : undefined}
                                    isFavorite={Boolean(newBuilding.isFavorite)}
                                    renderMortgageButton={renderMortgageButton}
                                    m2Pro={newBuilding.m2Pro}
                                />
                            ) : (
                                <NewBuildingCardRightBlockVtb
                                    developer={developerVtb}
                                    priceMin={newBuilding.priceMin}
                                    pricePerM2Min={newBuilding.pricePerM2Min}
                                    hasApartments={hasApartments}
                                />
                            )}
                        </div>
                    </div>
                </ChatStateProvider>
            </PhoneButtonStateProvider>
            {isOpenMortgageStarterModal && (
                <Mortgage2StarterModal
                    withoutAuth
                    isNewVibe
                    isPreferentialNbCondition
                    showMoreBankIcons
                    isDefaultSelectedOffer
                    gtmSendId={DATA_GTM.MORTGAGE_RIGHT_BLOCK_STARTER_MODAL_SEND_ID}
                    gtmShowId={DATA_GTM.MORTGAGE_RIGHT_BLOCK_STARTER_MODAL_SHOW_ID}
                    locationPlacement='плавающая кнопка'
                    onClose={closeMortgageStarterModal}
                    applicationId={ApplicationIdEnum.NewBuildingsDesktop}
                    realtyType='ProfOfferFlat'
                    mortgageRegionId={newBuilding.mortgageParams.regionId}
                    mortgageOffers={offersFull}
                    term={newBuilding.mortgageParams.term}
                    ownAmount={newBuilding.mortgageParams.ownAmount}
                    propertyCost={newBuilding.mortgageParams.propertyCost}
                    purpose={purpose as UseBankMortgageFormProps['purpose']}
                    gtmSendData={{
                        event: 'nb_ib_form',
                        action: 'send',
                        page_type: 'card',
                        place: 'right'
                    }}
                    gtmShowData={{
                        event: 'nb_ib_form',
                        action: 'open',
                        page_type: 'card',
                        place: 'right'
                    }}
                    withoutShowContainer
                />
            )}
        </>
    );
};

NewBuildingConstruction.displayName = 'NewBuildingConstruction';

export default NewBuildingConstruction;
